/**
 * What an accounting model is worth: the valuation discipline's overview.
 *
 * This was the right-hand column of the accounting overview, under a "Valuation"
 * heading beside an "Accounting" one. That was the right design when there was one
 * screen for both - and its comment argued the case well: "Reading down one column
 * tells you what the company did; reading down the other tells you what somebody
 * thinks it is worth."
 *
 * A discipline is now chosen first, which makes the two-column screen the mixing the
 * separation exists to prevent. An accounting user has no reason to meet a
 * discounted cash flow, and a valuation has a discipline of its own to live in.
 *
 * So the column moved here whole, along with the three components that only it used.
 * Nothing about the figures changed; only which screen they appear on.
 *
 * This values a MODEL, from its finished statements. A valuation project with no
 * accounts behind it is `ValuationWorkspace`, which values six numbers a year.
 */

import { useState } from 'react';

import { formatMoney } from '../../../lib/currency';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import type { FinancialModelV2 } from '../../../types/financial-model-v2';
import {
  buildMethodColumns,
  ValuationResultsV2,
  ValuationSummaryV2,
} from './ValuationPanelsV2';
import { MethodsPanelV2, ValuationWarningsV2 } from './MethodsPanelV2';
import { PeersPanel } from './PeersPanel';
import { DiscountRatePanel } from './DiscountRatePanel';
import { BandsPanel } from './BandsPanel';

function money(value: number | null | undefined, currency: string): string {
  if (value == null) return '—';
  return formatMoney(value, currency);
}

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-gray-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

function ValuationAssumptions({
  model,
  currency,
  onClose,
}: {
  model: FinancialModelV2;
  currency: string;
  onClose: () => void;
}) {
  const cfg = model.settings.valuationConfig;
  const v = model.computed.valuation;
  const dcf = v?.dcf ?? null;
  const multiples = v?.multiples ?? null;

  const discountedYears: number[] = dcf?.annualCashFlows?.map((f: { year: number }) => f.year) ?? [];
  const historical = model.settings.historicalYears ?? [];
  const multiplesUsable = !!multiples && multiples.terminalEBITDA !== 0;

  const rows: { label: string; value: string; note?: string }[] = [
    {
      label: 'Discount rate (WACC)',
      value: `${(cfg.discountRate * 100).toFixed(1)}%`,
    },
    {
      label: 'Terminal growth',
      value: `${(cfg.terminalGrowthRate * 100).toFixed(1)}%`,
    },
    {
      label: 'Years discounted',
      value: discountedYears.length ? discountedYears.join(', ') : '—',
      note: historical.length
        ? `${historical.join(', ')} excluded as historical — a year that has already happened is not a future cash flow`
        : undefined,
    },
    {
      label: 'Terminal year',
      value: dcf && discountedYears.length
        ? String(discountedYears[discountedYears.length - 1])
        : String(cfg.terminalYear),
      note:
        discountedYears.length &&
        discountedYears[discountedYears.length - 1] !== cfg.terminalYear
          ? `Configured as ${cfg.terminalYear}, which falls outside this model's years, so the last modelled year is used`
          : undefined,
    },
    {
      label: 'Exit multiple (EV/EBITDA)',
      value: `${cfg.exitMultipleEVEBITDA.toFixed(1)}x`,
    },
    {
      label: 'Methods in the headline figure',
      value: [dcf ? 'DCF' : null, multiplesUsable ? 'Multiples' : null]
        .filter(Boolean)
        .join(' + ') || '—',
      note: multiples && !multiplesUsable
        ? 'Multiples produced no result — there is no EBITDA for the terminal year — so it is left out rather than averaged in as zero'
        : undefined,
    },
  ];

  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Valuation assumptions"
      onClick={onClose}
    >
      <div
        className="max-h-[80vh] w-full max-w-2xl overflow-auto rounded-lg bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              How this valuation was reached
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Every figure below is an assumption, not a fact. Change them under
              Settings.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-50"
          >
            Close
          </button>
        </div>

        <table className="w-full text-sm">
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-b border-gray-100 align-top">
                <th scope="row" className="w-1/2 py-2 pr-4 text-left font-medium text-gray-700">
                  {r.label}
                </th>
                <td className="py-2 text-gray-900">
                  {r.value}
                  {r.note && (
                    <div className="mt-0.5 text-xs text-gray-500">{r.note}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {dcf && (
          <div className="mt-4">
            <h4 className="mb-1 text-xs font-semibold text-gray-700">
              Where the value comes from
            </h4>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-gray-100">
                  <th scope="row" className="py-1.5 text-left font-medium text-gray-700">
                    Discounted cash flows
                  </th>
                  <td className="py-1.5 text-right tabular-nums text-gray-900">
                    {money(dcf.sumOfPVCashFlows, currency)}
                  </td>
                </tr>
                <tr className="border-b border-gray-100">
                  <th scope="row" className="py-1.5 text-left font-medium text-gray-700">
                    Terminal value, discounted
                  </th>
                  <td className="py-1.5 text-right tabular-nums text-gray-900">
                    {money(dcf.pvTerminalValue, currency)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="py-1.5 text-left font-semibold text-gray-800">
                    DCF enterprise value
                  </th>
                  <td className="py-1.5 text-right font-semibold tabular-nums text-gray-900">
                    {money(dcf.enterpriseValue, currency)}
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="mt-2 text-xs text-gray-500">
              The terminal value is usually the larger half, and it rests
              entirely on the growth and discount rates above.
            </p>
          </div>
        )}

        {model.forecastDistribution?.enterpriseValue && (
          <p className="mt-4 border-t border-gray-100 pt-3 text-xs text-gray-600">
            A probabilistic forecast has also been run. Its sampled enterprise
            value runs{' '}
            {money(model.forecastDistribution.enterpriseValue.p5, currency)} to{' '}
            {money(model.forecastDistribution.enterpriseValue.p95, currency)},
            with a median of{' '}
            {money(model.forecastDistribution.enterpriseValue.p50, currency)}.
            It projects costs from this company's own last actual cost base, so
            a wide gap against the figure above is worth understanding rather
            than averaging away.
          </p>
        )}
      </div>
    </div>
  );
}

function MethodBreakdown({
  valuation,
  currency,
  method,
}: {
  valuation: NonNullable<FinancialModelV2['computed']['valuation']>;
  currency: string;
  method: string;
}) {
  const m = valuation.multiples;
  const s = valuation.summary;
  const dcfEV = s.dcfEnterpriseValue;
  const multEV = s.multiplesEnterpriseValue;

  if (dcfEV == null && multEV == null) return null;

  // Zero EBITDA makes the multiples leg meaningless rather than small, and it
  // is excluded from the blend upstream. Saying so is better than showing a nil.
  const multiplesUsable = m != null && m.terminalEBITDA !== 0;

  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
      <h4 className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
        How the {method === 'both' ? 'blend' : 'valuation'} was reached
      </h4>
      <table className="min-w-full text-sm">
        <tbody className="divide-y divide-gray-100">
          {dcfEV != null && (
            <tr>
              <td className="px-4 py-2 text-gray-600">Discounted cash flow</td>
              <td className="px-4 py-2 text-right tabular-nums text-gray-900">
                {formatMoney(dcfEV, currency)}
              </td>
            </tr>
          )}
          {multEV != null && multiplesUsable && m && (
            <>
              <tr>
                <td className="px-4 py-2 text-gray-600">
                  Multiples, at {m.exitMultiple.toFixed(1)}× terminal EBITDA of{' '}
                  {formatMoney(m.terminalEBITDA, currency)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-900">
                  {formatMoney(multEV, currency)}
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 pl-8 text-gray-500">less net debt</td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                  {formatMoney(m.netDebt, currency)}
                </td>
              </tr>
            </>
          )}
          {multEV != null && !multiplesUsable && (
            <tr>
              <td className="px-4 py-2 text-gray-500" colSpan={2}>
                Multiples excluded — terminal EBITDA is nil, so the method says
                nothing rather than nothing much.
              </td>
            </tr>
          )}
          <tr className="bg-gray-50">
            <td className="px-4 py-2 font-medium text-gray-900">
              {method === 'both' ? 'Blended enterprise value' : 'Enterprise value'}
            </td>
            <td className="px-4 py-2 text-right font-medium tabular-nums text-gray-900">
              {formatMoney(s.blendedEnterpriseValue, currency)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function ComparableChecks({
  comparables,
  blendedEV,
  currency,
}: {
  comparables: { metric: string; multipleUsed: number; baseValue: number; impliedEV: number }[];
  blendedEV: number;
  currency: string;
}) {
  if (comparables.length === 0) return null;

  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
      <h4 className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
        Comparable cross-checks
      </h4>
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-xs text-gray-500">
            <th scope="col" className="px-4 py-1.5 text-left font-medium">Metric</th>
            <th scope="col" className="px-4 py-1.5 text-right font-medium">Implied EV</th>
            <th scope="col" className="px-4 py-1.5 text-right font-medium">vs this valuation</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {comparables.map((c) => {
            const delta = blendedEV ? c.impliedEV / blendedEV - 1 : null;
            return (
              <tr key={c.metric}>
                <td className="px-4 py-2 text-gray-600">
                  {c.metric} at {c.multipleUsed.toFixed(1)}×
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-900">
                  {formatMoney(c.impliedEV, currency)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-600">
                  {delta === null
                    ? '—'
                    : `${delta >= 0 ? '+' : ''}${(delta * 100).toFixed(0)}%`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-500">
        Cross-checks only. None of these changes the valuation above.
      </p>
    </div>
  );
}

export function ValuationOverviewV2() {
  const model = useFinancialModelV2Store((s) => s.model);
  const updateSetting = useFinancialModelV2Store((s) => s.updateSetting);
  const [showAssumptions, setShowAssumptions] = useState(false);

  if (!model?.computed) {
    return <div className="p-6 text-sm text-gray-500">No model loaded.</div>;
  }

  const currency = model.settings.baseCurrency;
  const { valuation } = model.computed;

  // The forecast's own valuation, when one has been run, so the comparison can put
  // it beside the model's own methods. Net debt and the share count come from the
  // model: the forecast returns an enterprise value and nothing else.
  const forecastEV =
    (model.forecastDistribution as { enterpriseValue?: Record<string, number> } | null)
      ?.enterpriseValue ?? null;
  const methodColumns = valuation
    ? buildMethodColumns({
        valuation,
        forecastEV,
        netDebt: valuation.dcf?.netDebt ?? valuation.multiples?.netDebt ?? null,
        sharesOutstanding: model.settings.valuationConfig.sharesOutstanding ?? null,
      })
    : [];

  return (
    <div className="p-6">
      {!valuation?.summary && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-4 text-sm text-gray-500">
          No valuation yet. It needs forecast years with figures in them — the
          accounts alone cannot be valued.
        </div>
      )}
          {!valuation?.summary && (
            <div className="rounded-lg border border-dashed border-gray-300 bg-white p-4 text-sm text-gray-500">
              No valuation yet. It needs forecast years with figures in them —
              the accounts alone cannot be valued.
            </div>
          )}

      {valuation?.summary && (
        <div>
          <div className="mb-3 flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-700">Valuation</h3>
            <button
              type="button"
              onClick={() => setShowAssumptions(true)}
              className="rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-50"
            >
              Assumptions
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Stat
              label="Enterprise value"
              value={money(valuation.summary.blendedEnterpriseValue, currency)}
              sub={`Method: ${model.settings.valuationConfig.method}`}
            />
            <Stat
              label="Equity value"
              value={money(valuation.summary.blendedEquityValue, currency)}
            />
            <Stat
              label="Share price"
              value={
                valuation.summary.blendedSharePrice == null
                  ? '—'
                  : formatMoney(valuation.summary.blendedSharePrice, currency, {
                      // A share price is the one figure here where the pence
                      // matter.
                      decimals: 2,
                    })
              }
            />
          </div>
          <MethodBreakdown
            valuation={valuation}
            currency={currency}
            method={model.settings.valuationConfig.method}
          />

          <ValuationResultsV2
            valuation={valuation}
            currency={currency}
            terminalYear={model.settings.valuationConfig.terminalYear}
          />

          <ValuationSummaryV2
            columns={methodColumns}
            currency={currency}
            derivedFromNetDebt={methodColumns.some((c) => c.name === 'AI forecast')}
          />

          <ComparableChecks
            comparables={valuation.comparables ?? []}
            blendedEV={valuation.summary.blendedEnterpriseValue}
            currency={currency}
          />

          {/* What the engine refused to credit, where the figure would have
              been. A terminal value silently dropped is as misleading as a
              negative one shown. */}
          <ValuationWarningsV2 warnings={valuation.warnings} />

          {/* The methods that suit this business, anchors first. */}
          <MethodsPanelV2 methods={model.computed.methods} currency={currency} />

          {/* Each method as a range, and what drives it. */}
          <BandsPanel modelId={model.id} version={model.version} currency={currency} />

          {/* The discount rate's parts, where it is built up. */}
          <DiscountRatePanel rate={model.computed.discountRate} />

          {/* Where the company sits among its corpus peers. Values nothing. */}
          <PeersPanel
            peers={model.computed.peers}
            onExclude={(number) => {
              const cfg = model.settings.valuationConfig;
              updateSetting('valuationConfig', {
                ...cfg,
                excludedPeers: [...(cfg.excludedPeers ?? []), number],
              });
            }}
          />

          {showAssumptions && (
            <ValuationAssumptions
              model={model}
              currency={currency}
              onClose={() => setShowAssumptions(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}
